<template>
  <div class="w-full">
    <div class="flex items-start w-full">
      <div class="relative flex-1">
        <span
          class="pointer-events-none absolute top-1/2 z-10 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg bg-base-200 text-base-content"
          :class="isRtl === true ? 'right-3' : 'left-3'"
        >
          <Icon icon="app:termsPackage" class="h-4 w-4" />
        </span>
        <CustomDropdownSelector
          :modelValue="modelValue"
          :options="selectorOptions"
          :multiple="true"
          class="w-full"
          :buttonClass="dropdownButtonClass"
          :placeholder="placeholder"
          :toneLike="true"
          dropdownStyle="max-height: 320px; overflow-y: auto;"
          @update:modelValue="handleSelectionChange"
        />
      </div>
      <button
        type="button"
        class="btn"
        style="margin-inline-start: 12px;"
        @click="openTermsPackages"
      >
        {{ t('termsPackages.open', 'Manage') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useRouter } from 'vue-router';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import CustomDropdownSelector from './CustomDropdownSelector.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Select terms packages' }
});

const emit = defineEmits(['update:modelValue', 'update:selectedTerms']);

const router = useRouter();
const { t, currentLanguage } = useI18n();
const availableTermsPackages = ref([]);
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true ? true : false);
const dropdownButtonClass = computed(() => isRtl.value === true ? 'min-h-[3rem] pr-12' : 'min-h-[3rem] pl-12');

const selectorOptions = computed(() => {
  return availableTermsPackages.value.map((item) => ({
    value: item.packageId,
    label: item.name,
    description: getPackageSummary(item),
    icon: 'app:termsPackage'
  }));
});

const selectedPackages = computed(() => {
  const selectedIds = Array.isArray(props.modelValue) === true ? props.modelValue : [];
  return selectedIds
    .map((packageId) => availableTermsPackages.value.find((item) => item.packageId === packageId))
    .filter((item) => item !== undefined && item !== null);
});

const selectedTerms = computed(() => {
  const terms = [];
  for (let index = 0; index < selectedPackages.value.length; index += 1) {
    const item = selectedPackages.value[index];
    const packageItems = getPackageTermItems(item);
    for (let termIndex = 0; termIndex < packageItems.length; termIndex += 1) {
      const normalizedTerm = packageItems[termIndex].term;
      if (normalizedTerm !== '' && terms.includes(normalizedTerm) !== true) {
        terms.push(normalizedTerm);
      }
    }
  }
  return terms;
});

function getPackageItems(item) {
  const packageItems = [];
  if (Array.isArray(item?.termItems) === true) {
    for (let index = 0; index < item.termItems.length; index += 1) {
      const normalizedTerm = typeof item.termItems[index]?.term === 'string' ? item.termItems[index].term.trim() : '';
      const normalizedDescription = typeof item.termItems[index]?.description === 'string' ? item.termItems[index].description.trim() : '';
      if (normalizedTerm !== '') {
        const existingIndex = packageItems.findIndex((entry) => entry.term === normalizedTerm && entry.spokenAs === '');
        if (existingIndex === -1) {
          packageItems.push({
            term: normalizedTerm,
            description: normalizedDescription,
            spokenAs: ''
          });
        } else if (packageItems[existingIndex].description === '' && normalizedDescription !== '') {
          packageItems[existingIndex].description = normalizedDescription;
        }
      }
    }
  }
  if (Array.isArray(item?.fineTuningItems) === true) {
    for (let index = 0; index < item.fineTuningItems.length; index += 1) {
      const normalizedTerm = typeof item.fineTuningItems[index]?.term === 'string' ? item.fineTuningItems[index].term.trim() : '';
      const normalizedSpokenAs = typeof item.fineTuningItems[index]?.spokenAs === 'string' ? item.fineTuningItems[index].spokenAs.trim() : '';
      if (normalizedTerm !== '' && normalizedSpokenAs !== '') {
        const existingIndex = packageItems.findIndex((entry) => entry.term === normalizedTerm && entry.spokenAs === normalizedSpokenAs);
        if (existingIndex === -1) {
          packageItems.push({
            term: normalizedTerm,
            description: '',
            spokenAs: normalizedSpokenAs
          });
        }
      }
    }
  }
  if (packageItems.length > 0) {
    return packageItems;
  }
  if (Array.isArray(item?.items) === true) {
    for (let index = 0; index < item.items.length; index += 1) {
      const entityType = typeof item.items[index]?.entityType === 'string' ? item.items[index].entityType.trim() : '';
      const normalizedTerm = typeof item.items[index]?.term === 'string' ? item.items[index].term.trim() : '';
      const normalizedDescription = typeof item.items[index]?.description === 'string' ? item.items[index].description.trim() : '';
      const normalizedSpokenAs = typeof item.items[index]?.spokenAs === 'string' ? item.items[index].spokenAs.trim() : '';
      if (normalizedTerm !== '') {
        const existingIndex = packageItems.findIndex((entry) => entry.term === normalizedTerm);
        if (existingIndex === -1) {
          packageItems.push({
            term: normalizedTerm,
            description: entityType === 'fineTuning' || normalizedSpokenAs !== '' ? '' : normalizedDescription,
            spokenAs: normalizedSpokenAs
          });
        } else {
          if ((entityType === 'fineTuning' || normalizedSpokenAs !== '') !== true && packageItems[existingIndex].description === '' && normalizedDescription !== '') {
            packageItems[existingIndex].description = normalizedDescription;
          }
          if (packageItems[existingIndex].spokenAs === '' && normalizedSpokenAs !== '') {
            packageItems[existingIndex].spokenAs = normalizedSpokenAs;
          }
        }
      }
    }
  }
  if (packageItems.length === 0 && Array.isArray(item?.terms) === true) {
    for (let index = 0; index < item.terms.length; index += 1) {
      const normalizedTerm = typeof item.terms[index] === 'string' ? item.terms[index].trim() : '';
      if (normalizedTerm !== '') {
        packageItems.push({
          term: normalizedTerm,
          description: typeof item?.description === 'string' ? item.description.trim() : '',
          spokenAs: ''
        });
      }
    }
  }
  if (packageItems.length === 0) {
    const normalizedTerm = typeof item?.term === 'string' ? item.term.trim() : '';
    if (normalizedTerm !== '') {
      packageItems.push({
        term: normalizedTerm,
        description: typeof item?.description === 'string' ? item.description.trim() : '',
        spokenAs: ''
      });
    }
  }
  return packageItems;
}

function getPackageTermItems(item) {
  const packageItems = [];
  if (Array.isArray(item?.termItems) === true) {
    for (let index = 0; index < item.termItems.length; index += 1) {
      const normalizedTerm = typeof item.termItems[index]?.term === 'string' ? item.termItems[index].term.trim() : '';
      const normalizedDescription = typeof item.termItems[index]?.description === 'string' ? item.termItems[index].description.trim() : '';
      if (normalizedTerm !== '') {
        const existingIndex = packageItems.findIndex((entry) => entry.term === normalizedTerm);
        if (existingIndex === -1) {
          packageItems.push({
            term: normalizedTerm,
            description: normalizedDescription
          });
        } else if (packageItems[existingIndex].description === '' && normalizedDescription !== '') {
          packageItems[existingIndex].description = normalizedDescription;
        }
      }
    }
  }
  if (packageItems.length > 0) {
    return packageItems;
  }
  if (Array.isArray(item?.items) === true) {
    for (let index = 0; index < item.items.length; index += 1) {
      const entityType = typeof item.items[index]?.entityType === 'string' ? item.items[index].entityType.trim() : '';
      const normalizedTerm = typeof item.items[index]?.term === 'string' ? item.items[index].term.trim() : '';
      const normalizedDescription = typeof item.items[index]?.description === 'string' ? item.items[index].description.trim() : '';
      const normalizedSpokenAs = typeof item.items[index]?.spokenAs === 'string' ? item.items[index].spokenAs.trim() : '';
      if (normalizedTerm !== '' && entityType !== 'fineTuning' && normalizedSpokenAs === '') {
        const existingIndex = packageItems.findIndex((entry) => entry.term === normalizedTerm);
        if (existingIndex === -1) {
          packageItems.push({
            term: normalizedTerm,
            description: normalizedDescription
          });
        } else if (packageItems[existingIndex].description === '' && normalizedDescription !== '') {
          packageItems[existingIndex].description = normalizedDescription;
        }
      }
    }
  }
  if (packageItems.length === 0 && Array.isArray(item?.terms) === true) {
    for (let index = 0; index < item.terms.length; index += 1) {
      const normalizedTerm = typeof item.terms[index] === 'string' ? item.terms[index].trim() : '';
      if (normalizedTerm !== '') {
        packageItems.push({
          term: normalizedTerm,
          description: typeof item?.description === 'string' ? item.description.trim() : ''
        });
      }
    }
  }
  return packageItems;
}

function getPackageSummary(item) {
  const packageItems = getPackageItems(item);
  if (packageItems.length === 0) {
    return '-';
  }
  return packageItems
    .slice(0, 2)
    .map((entry) => {
      if (entry.spokenAs !== '') {
        return `${entry.term} - ${entry.spokenAs}`;
      }
      if (entry.description !== '') {
        return `${entry.term} - ${entry.description}`;
      }
      return entry.term;
    })
    .join(' | ');
}

function getAuthToken() {
  const authCookie = document.cookie.split('; ').find((row) => row.startsWith('authToken=') === true);
  const jwtCookie = document.cookie.split('; ').find((row) => row.startsWith('jwtToken=') === true);
  const cookie = authCookie !== undefined && authCookie !== null ? authCookie : jwtCookie;
  if (cookie === undefined || cookie === null) {
    return '';
  }
  const value = cookie.split('=')[1];
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return value;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function loadTermsPackages() {
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/termsPackages`, { headers: getAuthHeaders() });
    availableTermsPackages.value = Array.isArray(response.data) === true ? response.data : [];
  } catch (error) {
    availableTermsPackages.value = [];
  }
}

function handleSelectionChange(value) {
  emit('update:modelValue', Array.isArray(value) === true ? value : []);
}

function openTermsPackages() {
  router.push('/termsPackages');
}

watch(selectedTerms, (value) => {
  emit('update:selectedTerms', Array.isArray(value) === true ? value : []);
}, { immediate: true });

onMounted(async () => {
  await loadTermsPackages();
});
</script>
